import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TreeView from "@mui/lab/TreeView";
import { IconButton, List, ListSubheader } from "@mui/material";
import _ from "lodash";
import {
  ReactElement,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";

import CreateCategoryDialog from "../../components/dialogs/category/create-category";
import CustomTreeItem from "../../components/tree-menu/custom/item";
import UserRole from "../../components/user-role/user-role";
import useText from "../../hooks/use-text";
import { CategoryContext } from "../../providers/category-provider";
import { FilesContext } from "../../providers/files-provider";
import { Flatten, flatten } from "../../utils/flatten";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

export default function CategoriesFilter(): ReactElement {
  const { strings } = useText(local);
  const { categories } = useContext(CategoryContext);
  const { filter, setFilter } = useContext(FilesContext);

  const [selectedCategoryId, setSelectedCategoryId] = useState<string>();
  const [editCategoryMode, setEditCategoryMode] = useState(false);
  const [showCreateCategoryDialog, setShowCreateCategoryDialog] =
    useState(false);

  function handleCategoryChange(id: string): void {
    if (selectedCategoryId === id) {
      setFilter({
        ...filter,
        categoryId: undefined,
      });
      setSelectedCategoryId(undefined);
      return;
    }

    // ToDo: also add subcategories ids if there are exists
    setFilter({
      ...filter,
      categoryId: {
        eq: id.toString(),
      },
    });
  }

  function orderByIndex(array: Flatten[]): Flatten[] {
    return _.orderBy(array, ["order_index"], ["asc"]);
  }

  useEffect(() => {
    // Todo: Fix not updating the selected state
    if (filter && filter.categoryId) {
      setSelectedCategoryId(filter.categoryId.eq?.toString() || undefined);
    } else {
      setSelectedCategoryId(undefined);
    }
  }, [filter]);

  return (
    <div className="basis-[270px] shrink-0">
      <List
        sx={{ bgcolor: "background.paper" }}
        className="shadow max-w-[260px] width-full rounded-[15px] overflow-hidden pt-[8px] mb-[25px] ml-[45px]"
        component="nav"
        aria-labelledby="nested-list-subheader"
        subheader={
          <ListSubheader component="div" id="nested-list-subheader">
            <div className="flex justify-between">
              {strings.header}
              <UserRole>
                <div>
                  <IconButton
                    color={editCategoryMode ? "error" : undefined}
                    className="h-fit mr-10"
                    size="small"
                    onClick={() => setEditCategoryMode(!editCategoryMode)}
                  >
                    <EditOutlinedIcon className="!text-[15px]" />
                  </IconButton>
                  <IconButton
                    className="h-fit"
                    size="small"
                    onClick={() => setShowCreateCategoryDialog(true)}
                  >
                    <AddIcon fontSize="inherit" />
                  </IconButton>
                </div>
              </UserRole>
            </div>
          </ListSubheader>
        }
      >
        <TreeView
          className="flex-row-reverse"
          aria-label="icon expansion"
          defaultCollapseIcon={<ExpandLess className="!text-[22px]" />}
          defaultExpandIcon={<ExpandMore className="!text-[22px]" />}
          selected={selectedCategoryId}
          sx={styles}
          onNodeSelect={(_: SyntheticEvent, nodeId: string): void =>
            handleCategoryChange(nodeId)
          }
        >
          {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            orderByIndex(
              flatten(
                categories.map((v): Flatten => {
                  return {
                    id: v.id,
                    name: v.name || "",
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    parent_id: v.parentId,
                    order_index: v.orderIndex,
                    children: [],
                  };
                })
              )
            ).map((category) => {
              return (
                <CustomTreeItem
                  className="first:border-0"
                  key={category.id}
                  nodeId={category.id.toString()}
                  label={category.name}
                  classes={{
                    selected: editCategoryMode ? "editable" : "",
                  }}
                >
                  {orderByIndex(category.children).map((family: any) => {
                    return (
                      <CustomTreeItem
                        key={family.id}
                        nodeId={family.id.toString()}
                        label={family.name}
                        classes={{
                          selected: editCategoryMode ? "editable" : "",
                        }}
                      >
                        {orderByIndex(family.children).map((product: any) => {
                          if (product.parent_id === family.id) {
                            return (
                              <CustomTreeItem
                                key={product.id}
                                nodeId={product.id.toString()}
                                label={product.name}
                                classes={{
                                  selected: editCategoryMode ? "editable" : "",
                                }}
                              />
                            );
                          }
                        })}
                      </CustomTreeItem>
                    );
                  })}
                </CustomTreeItem>
              );
            })
          }
          <CreateCategoryDialog
            isOpen={showCreateCategoryDialog}
            onClose={() => setShowCreateCategoryDialog(false)}
          />
        </TreeView>
      </List>
    </div>
  );
}

type LocalSchema = {
  header: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    header: "Categories",
  },
};

const styles = {
  "& .MuiTreeItem-root": { borderTop: "1px solid silver" },
  "& .MuiCollapse-root": { margin: 0 },
  "& .MuiCollapse-root li > div": { paddingLeft: "30px" },
  "& .MuiCollapse-root li li > div": { paddingLeft: "50px" },
  "& .MuiTreeItem-root > div": {
    flexDirection: "row-reverse",
    padding: "11px 16px",
  },
  "& .MuiTypography-root": { fontSize: "14px!important" },
  '& > .MuiTreeItem-root[aria-expanded="true"] .Mui-selected + ul': {
    background: "#dd21270d",
  },
};
